import React from 'react'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'

import {
    StyledLandscapeStacked,
    StyledLink,
    StyledHeader,
    StyledSponsoredLabel,
    StyledText,
    StyledTeaser,
    StyledHeadline,
    StyledMedia,
    StyledHeadlineSkeleton,
} from 'component-library/cards/LandscapeStacked/LandscapeStacked.styled'

import { Theme } from 'component-library/__styling/themes'

import { toLinkWithHint } from 'common/routing/route-hints'
import { Product } from 'component-library/__product'
import { Section } from 'component-library/__styling/settings/sections'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { FixedRatio, CardBreakpointRatios } from 'common/client-data-types/image-item'
import { MediaMode } from '../CardMedia/CardMedia'
import { useProduct } from 'common/hooks/useProduct'
import { createCardClickedEvent } from 'common/events/collection-events'
import {
    PublicationCardItem,
    isPublicationCardItemWithVideo,
} from 'common/client-data-types/card-types'

export interface Props extends CommonCardProps {
    fixedRatio?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    cardNumber: number
    innerRef?: React.RefObject<any>
    item: MaybeLoaded<PublicationCardItem>
    mediaMode?: MediaMode
}

export interface TimeStampProps {
    section?: Section
    theme: Theme
}

export const LandscapeStacked: React.FC<Props> = props => {
    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!props.item.loaded) {
            e.preventDefault()
            return
        }

        props.onEvent(
            createCardClickedEvent(
                props.item.result,
                'InternalLandscapeStacked',
                'LandscapeStacked',
                props.cardNumber
            )
        )
    }

    const product = useProduct()
    const { fixedRatio = '4:3' } = props
    const showSponsoredStyles = props.item.loaded && props.item.result.isSponsored

    // Sponsored cards in perthnow do not display kickers
    const isPerthNowSponsored = showSponsoredStyles && product === 'perthnow'
    const sponsoredSevenByline = showSponsoredStyles && product === 'sevennews'
    const hideKicker = isPerthNowSponsored || props.hideKicker
    const cardItem = props.item

    const getImageWidth = (product: Product) => {
        switch (product) {
            case Product.PerthNow:
                return 106
            case Product.TheWest:
                return 110 // This card not used on the west atm
            case Product.SevenNews:
                return 126
        }
    }

    return (
        <StyledLandscapeStacked
            ref={props.innerRef}
            className={props.className}
            data-topic={props.item.loaded && props.item.result.primaryTopic.id}
        >
            <StyledLink
                to={toLinkWithHint(props.item)}
                onClick={linkClicked}
                isLoading={!props.item.loaded}
                hasBackground={false}
            >
                <StyledMedia
                    item={cardItem}
                    imageWidths={getImageWidth(product)}
                    fixedRatio={fixedRatio}
                    onEvent={props.onEvent}
                    disableImageLazyLoad={props.disableImageLazyLoad}
                    mediaMode={props.mediaMode}
                />

                <StyledHeader>
                    {cardItem.loaded ? (
                        <StyledHeadline
                            kickerText={cardItem.result.kicker}
                            headlineText={cardItem.result.shortHeadline}
                            hideKicker={hideKicker}
                            fontScale={product === 'perthnow' ? 1.2 : 0.88}
                            isLive={cardItem.result.isLive}
                            showVideoIcon={isPublicationCardItemWithVideo(cardItem.result)}
                            isSponsored={cardItem.result.isSponsored}
                            publicationKind={cardItem.result.publicationKind}
                            kickerVerticalSpacing={product === 'perthnow' ? 'sm' : 'xs'}
                        />
                    ) : (
                        <StyledHeadlineSkeleton
                            count={3}
                            fontScale={product === 'perthnow' ? 1.2 : 0.88}
                        />
                    )}
                    {/* sevennews sponsored label */}
                    {sponsoredSevenByline && cardItem.loaded && (
                        <StyledSponsoredLabel source={cardItem.result.byline} />
                    )}
                </StyledHeader>
                {props.hideTeaser ? null : (
                    <React.Fragment>
                        <StyledText sponsoredStyles={showSponsoredStyles}>
                            <StyledTeaser
                                teaserOrSkeletonLines={cardItem.loaded ? cardItem.result.teaser : 3}
                            />
                        </StyledText>
                        {showSponsoredStyles && !sponsoredSevenByline && cardItem.loaded && (
                            <StyledSponsoredLabel source={cardItem.result.byline} />
                        )}
                    </React.Fragment>
                )}
            </StyledLink>
        </StyledLandscapeStacked>
    )
}
LandscapeStacked.displayName = 'LandscapeStacked'
